import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faEdit, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { LineItemDeleteModalV2 } from '@/features/line-items/line-item-delete-modal';
import { useCaptureLineItemCreate } from '@/utils/posthog/analytics';
import { trpc } from '@/utils/trpc-client';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { Tooltip } from 'react-tooltip';

type Props = {
  data: LineItemTrpcResponse;
  setIsEditing: (lineItemId: string) => void;
};

export function LineItemActionsV2({ data, setIsEditing }: Props) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { mutateAsync, isPending } = trpc.duplicateLineItem.useMutation();
  const utils = trpc.useUtils();
  const captureCreate = useCaptureLineItemCreate();
  // Deletion is not possible if a lineitem has an execution with media plans
  const disableDelete = data.execution_status == 'execution_with_media_plans';

  async function handleDuplicateLineItem(lineItem: LineItemTrpcResponse) {
    if (isPending) return;
    const created = await mutateAsync({
      strategy_id: lineItem.strategy_id,
      line_item_id: lineItem.id
    });
    captureCreate(created.id, 'duplication');
    await utils.getProposalLineItems.invalidate();
    await utils.proposalDiff.invalidate();
  }

  return (
    <div className="relative">
      <div
        className={
          'shadow-l-lg fixed right-0 h-full border-l border-gray-200 bg-white px-2 transition-[width]'
        }>
        <div className="flex h-full w-full items-center justify-center gap-1.5">
          <LineItemAction
            className="bg-blue-600 hover:bg-blue-500"
            onClick={() => setIsEditing(data.id)}
            icon={<FontAwesomeIcon icon={faEdit} />}
            data-testid={`edit-line-item-${data.id}`}
          />
          <LineItemAction
            className="bg-blue-600 hover:bg-blue-500"
            onClick={() => handleDuplicateLineItem(data)}
            icon={<FontAwesomeIcon icon={faCopy} />}
          />
          <div id={`delete-line-item-${data.id}`}>
            <LineItemAction
              onClick={() => setDeleteModalOpen(true)}
              disabled={disableDelete}
              className="bg-red-600 hover:bg-red-500 disabled:bg-gray-300 disabled:text-gray-400"
              icon={<FontAwesomeIcon icon={faTrashCan} />}
            />
          </div>
          {disableDelete ? (
            <Tooltip
              anchorSelect={`#delete-line-item-${data.id}`}
              place="bottom"
              positionStrategy="fixed"
              style={{ zIndex: 9999 }}
              delayShow={300}>
              Remove all media plans first to enable deletion.
            </Tooltip>
          ) : null}
        </div>
      </div>
      <LineItemDeleteModalV2
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        lineItemsToDelete={[data]}
      />
    </div>
  );
}

type LineItemActionProps = {
  className: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

function LineItemAction({
  className,
  disabled = false,
  icon,
  onClick,
  ...props
}: LineItemActionProps) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center rounded-sm p-1 text-white ${className}`}
      {...props}>
      {icon}
    </button>
  );
}
